import { StyleSheet } from 'aphrodite';
import { THEME } from '../../components-v2/Theme/theme';
import { CssSelectors, Layouts } from '../../styles/styles';

export const styleSheet = StyleSheet.create({
	deleteKeysButton: {
		backgroundColor: THEME.colors.danger.main,
		color: THEME.colors.danger.contrastText,
		border: `1px solid ${THEME.colors.danger.main}`,
		boxShadow: '0 1px 2px 0 #f9f9f9',
		transition: 'color .5s, background-color .5s',
		':active': {
			background: THEME.colors.danger.dark,
		},
		':hover': {
			background: THEME.colors.danger.dark,
			border: `1px solid ${THEME.colors.danger.light}`,
			color: THEME.colors.danger.contrastText,
		},
	},
	billingIcon: {
		display: 'flex',
		flexDirection: 'column',
		justifyContent: 'flex-end',
	},
	buttonContainer: {
		...Layouts.horizontalStack(10),
	},
	container: {},
	formFieldLabel: {
		color: THEME.colors.text.light,
		display: 'inline-block',
		fontSize: '0.875rem',
		width: 200,
	},
	formFieldLabelWide: {
		width: '100%',
	},
	formFieldValue: {
		color: THEME.colors.text.main,
		fontSize: '0.875rem',
	},
	root: {
		padding: '1.25rem 0 7.5rem 0',
		...CssSelectors.allChildren(
			{
				marginTop: '1.25rem',
			},
			':not(:first-child)'
		),
	},
	mainSectionTitle: {
		marginBottom: THEME.spacing.md,
		marginTop: 0,
	},
	sectionTitle: {
		margin: 0,
		fontSize: THEME.typography.fontSizes.md,
	},
	sectionLead: {
		color: THEME.colors.text.main,
		marginTop: THEME.spacing.sm,
		marginBottom: THEME.spacing.sm,
		fontSize: THEME.typography.fontSizes.sm,
	},
	paymentSettings: {
		...CssSelectors.allChildren({}, 'div'),
	},
	customCheckbox: {
		// Remove the global override since we'll use a wrapper span
		transition: 'all 0.15s ease-in-out',
	},
	checkboxLabel: {
		color: THEME.colors.text.main,
		fontSize: 14,
		marginLeft: 20,
		transition: 'opacity 0.15s ease-in-out',
	},
	businessHoursContainer: {
		transition: 'opacity 0.15s ease-in-out',
	},
	businessHoursToggleContainer: {
		display: 'flex',
		alignItems: 'center',
		gap: '12px',
	},
	businessHoursExplanation: {
		backgroundColor: THEME.colors.info.light,
		color: THEME.colors.info.contrastText,
		fontSize: '0.8125rem',
		lineHeight: 1.4,
		maxWidth: 800,
		marginBottom: 10,
		padding: 12,
		borderRadius: 4,
	},
	validation: {
		color: THEME.colors.danger.main,
	},
	paymentContainer: {
		display: 'grid',
		gap: THEME.spacing.lg,
		width: '100%',
		gridTemplateColumns: '1fr',

		[`@media(min-width: ${THEME.breakpoints.xl}px)`]: {
			gridTemplateColumns: '1fr 1px 1fr',
		},
	},
	verticalDivider: {
		backgroundColor: THEME.colors.border.light,
		flexShrink: 0,

		height: 1,
		width: '100%',

		[`@media(min-width: ${THEME.breakpoints.xl}px)`]: {
			width: 1,
			height: '100%',
		},
	},
	stripeKeysSection: {
		minWidth: 0,
	},
	processingFeesSection: {
		marginTop: THEME.spacing.lg,
		minWidth: 0,
	},
	manageLinkSeparator: {
		color: THEME.colors.text.light,
		margin: '0 8px',
	},
	usersSection: {
		marginTop: THEME.spacing.md,
		paddingBottom: THEME.spacing.md,
	},
	subsection: {
		marginBottom: THEME.spacing.md,
	},
	subsectionTitle: {
		fontSize: 14,
		fontWeight: 600,
		color: THEME.colors.text.main,
		margin: `0 0 ${THEME.spacing.xs}px 0`,
	},
	subsectionDescription: {
		fontSize: 14,
		color: THEME.colors.text.light,
		marginBottom: 8,
	},
	// Branding settings styles
	brandingSection: {
		borderTop: `1px solid ${THEME.colors.border.light}`,
		marginTop: THEME.spacing.lg,
		paddingTop: THEME.spacing.lg,
	},
	brandingSectionTitle: {
		fontSize: THEME.typography.fontSizes.md,
		fontWeight: 600,
		color: THEME.colors.text.main,
		marginBottom: THEME.spacing.md,
		marginTop: 0,
	},
	brandingSubsection: {
		marginBottom: THEME.spacing.lg,
	},
	brandingSubsectionTitle: {
		fontSize: 14,
		fontWeight: 600,
		color: THEME.colors.text.main,
		marginBottom: THEME.spacing.sm,
	},
	brandingColorsRow: {
		display: 'flex',
		gap: THEME.spacing.lg,
		flexWrap: 'wrap' as const,
	},
	brandingColorItem: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: THEME.spacing.xs,
	},
	brandingColorLabel: {
		fontSize: 12,
		color: THEME.colors.text.light,
	},
	brandingColorWithClear: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	brandingImageList: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: THEME.spacing.sm,
	},
	brandingImageItem: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.md,
		padding: THEME.spacing.sm,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: 8,
		backgroundColor: THEME.colors.background.light,
	},
	brandingImagePreview: {
		width: 80,
		height: 80,
		objectFit: 'cover' as const,
		borderRadius: 4,
		backgroundColor: THEME.colors.background.main,
		flexShrink: 0,
	},
	brandingImagePlaceholder: {
		width: 80,
		height: 80,
		borderRadius: 4,
		backgroundColor: THEME.colors.background.main,
		border: `1px dashed ${THEME.colors.border.light}`,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		fontSize: 11,
		color: THEME.colors.text.light,
		flexShrink: 0,
	},
	brandingImageInfo: {
		flex: 1,
		minWidth: 0,
	},
	brandingImageName: {
		fontSize: 14,
		fontWeight: 500,
		color: THEME.colors.text.main,
		marginBottom: 2,
	},
	brandingImageDescription: {
		fontSize: 12,
		color: THEME.colors.text.light,
		overflow: 'hidden',
		textOverflow: 'ellipsis',
		whiteSpace: 'nowrap' as const,
	},
	brandingTextarea: {
		width: '100%',
		maxWidth: 600,
		minHeight: 100,
		padding: THEME.spacing.sm,
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: 8,
		fontSize: 14,
		fontFamily: 'inherit',
		boxSizing: 'border-box',
		resize: 'vertical' as const,
		':focus': {
			outline: 'none',
			borderColor: THEME.colors.primary.main,
		},
	},
	brandingFontSelect: {
		maxWidth: 400,
	},
	brandingAddImageButton: {
		marginTop: THEME.spacing.sm,
	},
	brandingEmptyState: {
		color: THEME.colors.text.light,
		fontSize: 14,
		fontStyle: 'italic',
	},
	brandingCustomColorsSection: {
		marginTop: THEME.spacing.md,
	},
	brandingCustomColorsList: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: THEME.spacing.sm,
		marginTop: THEME.spacing.sm,
		marginBottom: THEME.spacing.sm,
	},
	brandingCustomColorItem: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	brandingCustomColorName: {
		width: 150,
		padding: '6px 10px',
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: 4,
		fontSize: 14,
		':focus': {
			outline: 'none',
			borderColor: THEME.colors.primary.main,
		},
	},
	// Brand Images - View/Edit modes
	brandingImageRow: {
		border: `1px solid ${THEME.colors.border.light}`,
		borderRadius: 8,
		backgroundColor: THEME.colors.background.light,
		overflow: 'hidden',
	},
	brandingImageViewMode: {
		display: 'flex',
		alignItems: 'flex-start',
		justifyContent: 'space-between',
		padding: THEME.spacing.md,
		gap: THEME.spacing.md,
	},
	brandingImageViewContent: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: THEME.spacing.sm,
		flex: 1,
		minWidth: 0,
	},
	brandingImageEditMode: {
		padding: THEME.spacing.md,
	},
	brandingImageEditFields: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: THEME.spacing.md,
		marginBottom: THEME.spacing.md,
	},
	brandingImageEditActions: {
		display: 'flex',
		alignItems: 'center',
		gap: THEME.spacing.sm,
	},
	brandingImageFieldGroup: {
		display: 'flex',
		flexDirection: 'column' as const,
		gap: 4,
	},
	brandingImageFieldLabel: {
		fontSize: 12,
		fontWeight: 600,
		color: THEME.colors.text.light,
		textTransform: 'uppercase' as const,
		letterSpacing: '0.5px',
	},
	brandingImageFieldValue: {
		fontSize: 14,
		color: THEME.colors.text.main,
	},
	brandingImageNameLink: {
		fontSize: 14,
		color: THEME.colors.primary.main,
		background: 'none',
		border: 'none',
		padding: 0,
		cursor: 'pointer',
		textAlign: 'left' as const,
		textDecoration: 'underline',
		':hover': {
			color: THEME.colors.primary.dark,
		},
	},
	// Image Preview Modal
	imagePreviewModal: {
		backgroundColor: '#fff',
		maxWidth: '90vw',
		maxHeight: '90vh',
		display: 'flex',
		flexDirection: 'column' as const,
	},
	imagePreviewHeader: {
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'flex-start',
		padding: THEME.spacing.md,
		borderBottom: `1px solid ${THEME.colors.border.light}`,
	},
	imagePreviewTitle: {
		fontSize: 16,
		fontWeight: 600,
		color: THEME.colors.text.main,
	},
	imagePreviewFilename: {
		fontSize: 12,
		color: THEME.colors.text.light,
		marginTop: 2,
	},
	imagePreviewImg: {
		maxWidth: '100%',
		maxHeight: 'calc(90vh - 80px)',
		objectFit: 'contain' as const,
		display: 'block',
	},
	imagePreviewNoImage: {
		padding: THEME.spacing.xl,
		textAlign: 'center' as const,
		color: THEME.colors.text.light,
		fontSize: 14,
	},
	// Required field indicator
	requiredIndicator: {
		color: THEME.colors.danger.main,
		fontWeight: 400,
	},
	// Description field when empty (required validation)
	brandingImageDescriptionRequired: {
		borderColor: THEME.colors.warning.main,
	},
	// Helper hint text below fields
	brandingImageFieldHint: {
		fontSize: 12,
		color: THEME.colors.text.light,
		fontStyle: 'italic',
		marginTop: 4,
	},
	// Thumbnail in edit mode
	brandingImageEditThumbnail: {
		marginBottom: THEME.spacing.md,
		display: 'flex',
		justifyContent: 'center',
	},
	brandingImageEditThumbnailImg: {
		maxWidth: 200,
		maxHeight: 120,
		objectFit: 'contain' as const,
		borderRadius: 8,
		border: `1px solid ${THEME.colors.border.light}`,
		backgroundColor: THEME.colors.background.main,
	},
	// Button row with Clear All
	brandingButtonRow: {
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'space-between',
		flexWrap: 'wrap' as const,
		gap: THEME.spacing.md,
	},
	clearAllButton: {
		color: THEME.colors.danger.main,
		':hover': {
			color: THEME.colors.danger.dark,
		},
	},
	brandingContentWrapper: {
		position: 'relative' as const,
	},
	brandingLoadingOverlay: {
		position: 'absolute' as const,
		top: 0,
		left: 0,
		right: 0,
		bottom: 0,
		backgroundColor: 'rgba(255, 255, 255, 0.8)',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		zIndex: 1,
	},
});

/**
 * ReactModal style objects (not Aphrodite styles - ReactModal requires plain style objects)
 */
export const imagePreviewModalStyles = {
	overlay: {
		backgroundColor: 'rgba(0, 0, 0, 0.75)',
		zIndex: 1000,
	},
	content: {
		top: '50%',
		left: '50%',
		right: 'auto',
		bottom: 'auto',
		marginRight: '-50%',
		transform: 'translate(-50%, -50%)',
		padding: 0,
		border: 'none',
		borderRadius: 8,
		maxWidth: '90vw',
		maxHeight: '90vh',
		overflow: 'hidden',
	},
} as const;
